/**
 * POC animated lobby — motion tier.
 *
 * One attribute on <html>, `data-motion-tier="full" | "lite" | "off"`, decided once at boot by
 * core/services/managers/MotionTierManager and read by every effect (CSS through the attribute,
 * JS through `getMotionTier`). `full` plays everything, `lite` drops the canvas effects, the
 * staggers and shortens the DOM one-shots, `off` plays nothing (also forced by
 * prefers-reduced-motion). Pure module: no React, no store, no core imports.
 */

export type MotionTier = 'full' | 'lite' | 'off';

export const MOTION_TIER_ATTRIBUTE = 'data-motion-tier';
export const FX_ATTRIBUTE = 'data-fx';
const URL_OVERRIDE_PARAM = 'motion';

// Heuristic inputs for the default tier on a true mobile device. Physical pixels stand in for
// tile memory (a 390x844 phone at 3x is 2.8 M px; the iPhone XS, the floor we test on, is 2.7 M).
const LITE_MAX_PHYSICAL_PIXELS = 3_600_000;
const LITE_MAX_CORES = 4;

let currentTier: MotionTier = 'off';
const listeners = new Set<(tier: MotionTier) => void>();

export const isMotionTier = (value: unknown): value is MotionTier =>
  value === 'full' || value === 'lite' || value === 'off';

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const readUrlOverride = (): MotionTier | null => {
  try {
    const value = new URLSearchParams(window.location.search).get(URL_OVERRIDE_PARAM);
    return isMotionTier(value) ? value : null;
  } catch {
    return null;
  }
};

interface TierInputs {
  trueMobile: boolean;
}

/**
 * `?motion=full|lite|off` wins, then prefers-reduced-motion forces `off`; otherwise phones with few
 * cores or a very dense screen get `lite` and everything else `full`. Desktop is always `full`.
 */
export const resolveMotionTier = ({ trueMobile }: TierInputs): MotionTier => {
  const override = readUrlOverride();
  if (override) return override;
  if (prefersReducedMotion()) return 'off';
  if (!trueMobile) return 'full';

  const dpr = window.devicePixelRatio || 1;
  const physicalPixels = window.innerWidth * dpr * window.innerHeight * dpr;
  // Safari does not always expose hardwareConcurrency; unknown counts as plenty.
  const cores = navigator.hardwareConcurrency || 8;

  return cores <= LITE_MAX_CORES || physicalPixels > LITE_MAX_PHYSICAL_PIXELS ? 'lite' : 'full';
};

export const getMotionTier = (): MotionTier => currentTier;

/** Writes the tier to <html> and notifies subscribers (the QA panel re-tiers at runtime). */
export const applyMotionTier = (tier: MotionTier): void => {
  currentTier = tier;
  document.documentElement.setAttribute(MOTION_TIER_ATTRIBUTE, tier);
  listeners.forEach((listener) => listener(tier));
};

/** Removes every attribute the POC stamps on <html>. */
export const clearMotionTier = (): void => {
  currentTier = 'off';
  document.documentElement.removeAttribute(MOTION_TIER_ATTRIBUTE);
  document.documentElement.removeAttribute(FX_ATTRIBUTE);
};

export const subscribeMotionTier = (listener: (tier: MotionTier) => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
